import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Editable per-tenant Instructions / Manual / Policy panel.
// Reads+writes /api/module-policies/:moduleKey. Everyone can read; admins/managers edit.
//
// Usage: <PolicyTab moduleKey="payroll" label="Payroll Policy" />
export default function PolicyTab({ moduleKey, label = 'Instructions / Policy' }) {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canEdit = ['admin', 'manager'].includes(role);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState({ title: '', content: '' });
  const [doc, setDoc]           = useState(null);
  const [alert, setAlert]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/module-policies/${moduleKey}`);
      const data = r.data?.data || r.data || {};
      setDoc(data);
      // Pre-fill the editor with either saved content or the default template,
      // so admins can tweak and save without starting from a blank page.
      setForm({ title: data.title || '', content: data.content || '' });
    } catch (e) {
      setAlert({ type: 'danger', msg: e.response?.data?.message || 'Failed to load policy.' });
    }
    setLoading(false);
  }, [moduleKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/module-policies/${moduleKey}`, form);
      const data = r.data?.data || r.data;
      setDoc(data);
      setForm({ title: data.title || '', content: data.content || '' });
      setEditing(false);
      setAlert({ type: 'success', msg: 'Policy saved.' });
      setTimeout(() => setAlert(null), 3000);
    } catch (e) {
      setAlert({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSaving(false);
  };

  const cancel = () => {
    setForm({ title: doc?.title || '', content: doc?.content || '' });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border text-primary" />
      </div>
    );
  }

  const hasContent = !!(doc?.title || doc?.content);
  const isDefault = !!doc?.isDefault;
  const updatedBy = doc?.updatedBy
    ? `${doc.updatedBy.firstName || ''} ${doc.updatedBy.lastName || ''}`.trim()
    : '';

  return (
    <div>
      {alert && (
        <div className={`alert alert-${alert.type} py-2 d-flex justify-content-between align-items-center`} role="alert">
          <span>{alert.msg}</span>
          <button className="btn-close btn-sm" onClick={() => setAlert(null)} />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="fw-bold mb-0">
            <i className="bi bi-journal-text me-2 text-primary" />
            {label}
            {isDefault && (
              <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 11, verticalAlign: 'middle' }}>
                <i className="bi bi-info-circle me-1" />Default template
              </span>
            )}
          </h5>
          {doc?.updatedAt && !isDefault && (
            <small className="text-muted">
              Last updated {new Date(doc.updatedAt).toLocaleString()}
              {updatedBy ? ` by ${updatedBy}` : ''}
            </small>
          )}
          {isDefault && canEdit && (
            <small className="text-muted">
              This is the built-in default. Click <strong>Customize</strong> to edit and save your tenant's version.
            </small>
          )}
        </div>
        {canEdit && !editing && (
          <button className="btn btn-outline-primary btn-sm" onClick={() => setEditing(true)}>
            <i className="bi bi-pencil me-1" />{isDefault ? 'Customize' : (hasContent ? 'Edit' : 'Create')}
          </button>
        )}
      </div>

      {editing ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label fw-semibold">Title</label>
              <input
                className="form-control"
                placeholder="e.g. Payroll Calculation Policy — FY 2026-27"
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Content</label>
              <textarea
                className="form-control"
                rows={18}
                placeholder="Write your instructions / manual / policy here. Plain text or Markdown — line breaks are preserved."
                value={form.content}
                onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }}
              />
              <small className="text-muted">
                Basic Markdown is supported: <code>**bold**</code>, <code>*italic*</code>, <code># Heading</code>,
                <code>- list</code>. Line breaks are preserved.
              </small>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-outline-secondary" onClick={cancel} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : <><i className="bi bi-check-lg me-1" />Save</>}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {hasContent ? (
              <>
                {doc.title && <h4 className="fw-bold mb-3">{doc.title}</h4>}
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 15 }}>
                  {renderLightMarkdown(doc.content || '')}
                </div>
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-journal" style={{ fontSize: 40 }} />
                <p className="mt-3 mb-1 fw-semibold">No content available.</p>
                <p className="small mb-0">Contact your admin to define content for this module.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Very small markdown renderer — headings, bold, italic, lists. No HTML injection.
function renderLightMarkdown(text) {
  const lines = text.split('\n');
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(<ul key={`ul-${out.length}`} style={{ marginBottom: 12 }}>{listBuffer}</ul>);
      listBuffer = [];
    }
  };

  lines.forEach((line, i) => {
    const h = line.match(/^(#{1,4})\s+(.+)$/);
    const li = line.match(/^[-*]\s+(.+)$/);
    if (h) {
      flushList();
      const level = h[1].length;
      const Tag = `h${Math.min(level + 2, 6)}`;
      out.push(React.createElement(Tag, { key: i, style: { marginTop: 16, marginBottom: 8, fontWeight: 700 } }, inline(h[2])));
    } else if (li) {
      listBuffer.push(<li key={i}>{inline(li[1])}</li>);
    } else if (line.trim() === '') {
      flushList();
      out.push(<br key={i} />);
    } else {
      flushList();
      out.push(<p key={i} style={{ marginBottom: 8 }}>{inline(line)}</p>);
    }
  });
  flushList();
  return out;
}

// Inline formatting: **bold** and *italic*
function inline(text) {
  const parts = [];
  let rest = text;
  let idx = 0;
  const regex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
  let match, last = 0;
  while ((match = regex.exec(rest)) !== null) {
    if (match.index > last) parts.push(rest.slice(last, match.index));
    if (match[2]) parts.push(<strong key={idx++}>{match[2]}</strong>);
    else if (match[4]) parts.push(<em key={idx++}>{match[4]}</em>);
    last = regex.lastIndex;
  }
  if (last < rest.length) parts.push(rest.slice(last));
  return parts;
}
